import { useQuery } from '@tanstack/react-query'
import { cacheQueryData, getCachedQueryData } from '../utils/scoreQueue'
import { searchCourses, searchCoursesOnline, getCourseFromDb, fetchCourseOnline, getNearbyCourses } from '../api/courses'

/**
 * Search courses in Supabase (primary, instant results).
 */
export function useCourseSearch(query) {
  const cacheKey = query ? `courseSearch_${query}` : null
  return useQuery({
    queryKey: ['courses', 'search', query],
    queryFn: async () => {
      const data = await searchCourses(query)
      if (cacheKey) cacheQueryData(cacheKey, data)
      return data
    },
    enabled: !!query && query.length >= 3,
    staleTime: 5 * 60 * 1000, // 5 min — course data is static
    initialData: () => (cacheKey ? getCachedQueryData(cacheKey) : undefined),
    initialDataUpdatedAt: 0,
  })
}

/**
 * Search courses via API proxy (fallback, user-triggered).
 */
export function useOnlineCourseSearch(query, enabled) {
  return useQuery({
    queryKey: ['courses', 'online-search', query],
    queryFn: () => searchCoursesOnline(query),
    enabled: !!enabled && !!query && query.length >= 3,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch nearby courses using GPS coordinates.
 */
export function useNearbyCourses(lat, lng) {
  return useQuery({
    queryKey: ['courses', 'nearby', lat, lng],
    queryFn: () => getNearbyCourses(lat, lng),
    enabled: lat != null && lng != null,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Fetch full course details from Supabase (should always be there for pre-scraped courses).
 */
export function useCourseDetails(courseId) {
  return useQuery({
    queryKey: ['courses', 'detail', courseId],
    queryFn: () => getCourseFromDb(courseId),
    enabled: !!courseId,
    staleTime: 30 * 60 * 1000, // 30 min — course data is immutable
  })
}

/**
 * Fetch full course details from the API (for online search results).
 */
export function useOnlineCourseDetails(courseId, enabled) {
  return useQuery({
    queryKey: ['courses', 'online-detail', courseId],
    queryFn: () => fetchCourseOnline(courseId),
    enabled: !!enabled && !!courseId,
    staleTime: 30 * 60 * 1000,
  })
}
